import { useEffect } from 'react';
import { markBoot } from '../../shared/dev/bootTiming';
import { subscribeConnectivity } from '../connectivity/connectivityInfra';
import {
  resolveOperationalShellGate,
  type OperationalShellGate,
} from '../connectivity/appOperationalGate';
import { primeOperationalGateFromOfflineEvidence } from '../session/offlineBootstrapEvidence';

export function useOperationalGate({
  userId,
  session,
  accountSetupResolved,
  requiresOwnerOnboarding,
  pendingEmployeeInvitation,
  awaitingEmployeeInvitation,
  hasOrgAndBranches,
  hasBusinessProfile,
  offlineBootstrapEvidence,
  refreshOfflineBootstrapEvidence,
  refreshAccountSetup,
  initialAuthReady,
  initialBranchSelectionReady,
  employeeAccessReady,
}: {
  userId: string | undefined;
  session: boolean;
  accountSetupResolved: boolean;
  requiresOwnerOnboarding: boolean | null;
  pendingEmployeeInvitation: { isOwner: boolean } | null;
  awaitingEmployeeInvitation: boolean;
  hasOrgAndBranches: boolean;
  hasBusinessProfile: boolean;
  offlineBootstrapEvidence: boolean | null;
  refreshOfflineBootstrapEvidence: () => void;
  refreshAccountSetup: (options?: { force?: boolean; background?: boolean }) => Promise<void>;
  initialAuthReady: boolean;
  initialBranchSelectionReady: boolean;
  employeeAccessReady: boolean;
}): OperationalShellGate {
  const gate = resolveOperationalShellGate({
    session,
    accountSetupResolved,
    requiresOwnerOnboarding,
    pendingEmployeeInvitation,
    awaitingEmployeeInvitation,
    hasOrgAndBranches,
    hasBusinessProfile,
    offlineBootstrapEvidence,
    initialAuthReady,
    initialBranchSelectionReady,
    employeeAccessReady,
  });

  useEffect(() => {
    if (!userId) return;
    void primeOperationalGateFromOfflineEvidence(userId).then((primed) => {
      if (primed) {
        void refreshAccountSetup({ background: true });
      }
    });
  }, [userId, refreshAccountSetup]);

  useEffect(() => {
    if (accountSetupResolved && userId) {
      markBoot('account_setup_resolved');
    }
  }, [accountSetupResolved, userId]);

  useEffect(() => {
    if (!gate.bootstrapping && userId) {
      markBoot('boot_done');
    }
  }, [gate.bootstrapping, userId]);

  useEffect(() => {
    if (!userId || !gate.needsOfflineSetupHint) return;
    const unsub = subscribeConnectivity((status) => {
      if (!status.isOnline || !status.isInternetReachable) return;
      void refreshAccountSetup({ force: true, background: true })
        .then(() => refreshOfflineBootstrapEvidence())
        .catch(() => refreshOfflineBootstrapEvidence());
    });
    return unsub;
  }, [
    userId,
    gate.needsOfflineSetupHint,
    refreshAccountSetup,
    refreshOfflineBootstrapEvidence,
  ]);

  return gate;
}
